import { useModal } from '@billy/design-system'
import { useCallback, useRef } from 'react'

import { EInvoiceStatus } from '@features/eInvoicing/constants'
import { useLifecycleAction } from '@features/eInvoicing/hooks/useLifecycleAction'
import {
  useUpdateOrganizationSettings,
  useUserOrganization,
  useUserOrganizationSettings,
} from '@modules-deprecated/app/organization'
import { EInvoiceLifecycleTrackingEvents, type EInvoiceLifecycleTrackingSource } from '@views/receipts/enums/tracking'

import { ModalId } from '../../../enums/modalId'
import { useSegment } from '../../../hooks'

interface UseEInvoiceApproveActionProps {
  attachmentId?: string
  onSuccess?: () => void
  previousStatus?: EInvoiceStatus | null
  trackingSource: EInvoiceLifecycleTrackingSource
}

export const useEInvoiceApproveAction = ({
  attachmentId,
  onSuccess,
  previousStatus,
  trackingSource,
}: UseEInvoiceApproveActionProps) => {
  const { organization } = useUserOrganization()
  const {
    isLoading: isOrganizationSettingsLoading,
    isPreviousData: isOrganizationSettingsPreviousData,
    organizationSettings,
  } = useUserOrganizationSettings()
  const { update: updateOrganizationSettings } = useUpdateOrganizationSettings()
  const suppressCancelTrackingRef = useRef(false)
  const isProcessingRef = useRef(false)
  const { track } = useSegment()

  const previousStatusForTracking = previousStatus ?? EInvoiceStatus.Active
  const areOrganizationSettingsReady = !isOrganizationSettingsLoading && !isOrganizationSettingsPreviousData
  const isApprovalModalDismissed =
    areOrganizationSettingsReady && organizationSettings?.isEInvoiceApprovalModalDismissed === '1'

  const { close: closeApprovalModal, open: openApprovalModal } = useModal(ModalId.EInvoiceApprovalConfirmationModal, {
    onClose: () => {
      if (suppressCancelTrackingRef.current || isProcessingRef.current) {
        return
      }

      track(EInvoiceLifecycleTrackingEvents.ActionCancelled, {
        action: 'approve',
        source: trackingSource,
        previousStatus: previousStatusForTracking,
      })
    },
  })

  const { submit, isProcessing } = useLifecycleAction({
    action: 'approve',
    onSuccess: () => {
      track(EInvoiceLifecycleTrackingEvents.ActionSubmitted, {
        action: 'approve',
        source: trackingSource,
        previousStatus: previousStatusForTracking,
      })
      suppressCancelTrackingRef.current = true
      closeApprovalModal()
      suppressCancelTrackingRef.current = false
      onSuccess?.()
    },
  })

  isProcessingRef.current = isProcessing

  const submitApprove = useCallback(
    (shouldNeverShowAgain = false) => {
      const organizationId = organization?.id

      if (!attachmentId || !organizationId) {
        return
      }

      submit(
        { attachmentId, organizationId },
        {
          onSuccess: () => {
            if (shouldNeverShowAgain) {
              updateOrganizationSettings({
                key: 'isEInvoiceApprovalModalDismissed',
                organizationId,
                value: '1',
              })
            }
          },
        },
      )
    },
    [attachmentId, organization?.id, submit, updateOrganizationSettings],
  )

  const handleApproveClick = useCallback(() => {
    track(EInvoiceLifecycleTrackingEvents.ApproveCTAClicked, { source: trackingSource })

    if (isApprovalModalDismissed) {
      submitApprove()
      return
    }

    openApprovalModal()
  }, [isApprovalModalDismissed, openApprovalModal, submitApprove, track, trackingSource])

  const handleConfirmApproval = useCallback(
    ({ shouldNeverShowAgain }: { shouldNeverShowAgain: boolean }) => {
      submitApprove(shouldNeverShowAgain)
    },
    [submitApprove],
  )

  return {
    handleApproveClick,
    handleConfirmApproval,
    isProcessing,
  }
}
